import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const AboutSection = () => {
  return (
    <section data-testid="about-section" className="relative py-24 md:py-32 bg-[#0C0C0E] overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-pomegranate/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Image Column with Overlapping */}
          <div className="relative order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <img
                src="https://images.unsplash.com/photo-1709396759771-07c3644794c8?w=800&q=80"
                alt="Chef preparing food"
                className="w-full max-w-lg rounded-2xl"
              />
            </motion.div>

            {/* Overlapping Decorative Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="absolute -bottom-8 -right-8 md:right-12 z-20 bg-surface/80 backdrop-blur-sm border border-white/5 rounded-xl p-6 max-w-[200px]"
            >
              <p className="font-accent text-saffron text-3xl mb-2">10+</p>
              <p className="font-body text-white/60 text-sm">Years of culinary excellence</p>
            </motion.div>

            {/* Background Pattern */}
            <div className="absolute -top-8 -left-8 w-full h-full border border-saffron/20 rounded-2xl -z-10" />
          </div>

          {/* Text Column */}
          <div className="order-1 lg:order-2 space-y-8">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-accent text-saffron text-xl mb-3"
              >
                Our Story
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-heading text-4xl md:text-5xl text-white leading-tight"
              >
                From Tehran <br />
                <span className="italic text-saffron">to Your Table</span>
              </motion.h2>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="font-body text-white/60 text-base md:text-lg leading-relaxed"
            >
              Cafe Irani is more than a restaurant — it's a journey through the rich tapestry of Iranian cuisine. 
              Our recipes, passed down through generations, bring the authentic flavors of Iran to your plate, 
              prepared with the finest ingredients and unwavering passion.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="font-body text-white/60 text-base md:text-lg leading-relaxed"
            >
              Every dish tells a story of tradition, from the slow-cooked stews of the Caspian 
              to the saffron-infused rice of Isfahan. We invite you to experience the warmth 
              of Iranian hospitality in every bite.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-8 pt-4"
            >
              <div>
                <p className="font-heading text-3xl text-saffron">50+</p>
                <p className="font-body text-white/40 text-sm">Authentic Dishes</p>
              </div>
              <div>
                <p className="font-heading text-3xl text-saffron">100%</p>
                <p className="font-body text-white/40 text-sm">Fresh Ingredients</p>
              </div>
              <div>
                <p className="font-heading text-3xl text-saffron">5k+</p>
                <p className="font-body text-white/40 text-sm">Happy Guests</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-heading italic text-lg hover:bg-white/10 transition-all duration-300"
              >
                Get in Touch
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
